import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Star } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { SEOMetaTags } from '@/components/SEOMetaTags';
import { supabase } from '@/integrations/supabase/client';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"

interface ReviewFormData {
  rating: number;
  comments?: string;
}

const FeedbackPage = () => {
  const { isAuthenticated, user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rating, setRating] = useState<number | null>(null);
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [appointment, setAppointment] = useState<any>(null);
  const [isReviewable, setIsReviewable] = useState(false);

  useEffect(() => {
    const fetchAppointment = async () => {
      if (!params.id) {
        toast({
          title: 'Fehler',
          description: 'Termin-ID fehlt.',
          variant: 'destructive',
        });
        return;
      }

      try {
        // Mock appointment data since we don't have a bookings table in the database
        // In a real app, this would fetch from the bookings table
        const mockBooking = {
          id: params.id,
          studio_id: 'studio123',
          service_id: 'service456',
          staff_id: 'staff789'
        };
        setAppointment(mockBooking);
      } catch (error: any) {
        console.error('Error fetching appointment:', error);
        toast({
          title: 'Fehler',
          description: 'Termin konnte nicht geladen werden.',
          variant: 'destructive',
        });
      }
    };

    fetchAppointment();
  }, [params.id, toast]);

  useEffect(() => {
    const checkReviewable = async () => {
      if (!params.id || !user) return;

      try {
        const { data: existingReviews } = await supabase
          .from('reviews')
          .select('*')
          .eq('appointment_id', params.id as string)
          .eq('user_id', user.id as string);

        setIsReviewable(!existingReviews || existingReviews.length === 0);
      } catch (error: any) {
        console.error('Error checking existing reviews:', error);
        toast({
          title: 'Fehler',
          description: 'Konnte nicht prüfen, ob eine Bewertung möglich ist.',
          variant: 'destructive',
        });
        setIsReviewable(false);
      }
    };

    checkReviewable();
  }, [params.id, user, toast]);

  const handleSubmit = async (data: ReviewFormData) => {
    setSubmitting(true);
    try {
      const appointmentId = params.id;
      
      if (!appointmentId) {
        throw new Error('Appointment ID is missing');
      }
      
      // Check if a review already exists
      const { data: existingReviews } = await supabase
        .from('reviews')
        .select('*')
        .eq('appointment_id', appointmentId as string)
        .eq('user_id', user?.id as string);
      
      if (existingReviews && existingReviews.length > 0) {
        toast({
          title: 'Bewertung existiert bereits',
          description: 'Du hast diesen Termin bereits bewertet.',
          variant: 'destructive',
        });
        setSubmitting(false);
        return;
      }
      
      // Insert the review with type assertion to fix type issue
      const { data: newReview, error } = await supabase
        .from('reviews')
        .insert({
          appointment_id: appointmentId as string,
          user_id: user?.id as string,
          studio_id: appointment?.studio_id || '',
          service_id: appointment?.service_id || null,
          staff_id: appointment?.staff_id || null,
          rating: data.rating,
          comment: data.comments || null,
        } as any)
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast({
        title: 'Bewertung gespeichert',
        description: 'Vielen Dank für dein Feedback!',
      });
      navigate('/bookings');
    } catch (error: any) {
      console.error('Error submitting review:', error);
      toast({
        title: 'Fehler',
        description: 'Bewertung konnte nicht gespeichert werden.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container py-8">
        <Card>
          <CardHeader>
            <CardTitle>Anmeldung erforderlich</CardTitle>
            <CardDescription>Bitte melde dich an, um Feedback zu geben.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/auth">Zum Login</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="container py-8">
        <Card>
          <CardHeader>
            <CardTitle>Termin nicht gefunden</CardTitle>
            <CardDescription>Der angeforderte Termin konnte nicht gefunden werden.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/bookings">Zurück zu meinen Buchungen</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isReviewable) {
    return (
      <div className="container py-8">
        <Card>
          <CardHeader>
            <CardTitle>Bewertung nicht möglich</CardTitle>
            <CardDescription>Du hast diesen Termin bereits bewertet.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/bookings">Zurück zu meinen Buchungen</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <SEOMetaTags title="Feedback geben" description="Gib Feedback zu deinem Termin" />
      <Card>
        <CardHeader>
          <CardTitle>Feedback geben</CardTitle>
          <CardDescription>Wie war dein Termin?</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="rating">Bewertung</Label>
            <RadioGroup defaultValue={rating?.toString()} className="flex gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <div className="flex items-center space-x-2" key={value}>
                  <RadioGroupItem value={value.toString()} id={`r${value}`} onClick={() => setRating(value)} />
                  <Label htmlFor={`r${value}`} className="cursor-pointer">
                    <Star className={`h-5 w-5 ${rating && value <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-400'}`} />
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="comments">Kommentare</Label>
            <Textarea
              id="comments"
              placeholder="Was war gut, was könnte verbessert werden?"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </CardContent>
        <CardContent>
          <Button disabled={submitting} onClick={() => handleSubmit({ rating: rating || 0, comments })} >
            {submitting ? 'Wird gespeichert...' : 'Feedback senden'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackPage;
